import React from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Award } from 'lucide-react';

/**
 * CeCertificatePreview - Professional certificate design
 * 
 * Pixel-perfect port from Polymet prototype with succession theme variables.
 */
const CeCertificatePreview = ({
  userName,
  courseName,
  creditHours,
  completionDate,
  sponsorName,
  certificateId,
  isPreview = false,
  className = '',
}) => {
  const intl = useIntl();
  return (
    <div
      className={`
        relative overflow-hidden
        bg-gradient-to-br from-succession-bg-primary to-succession-bg-secondary/30
        border border-succession-border rounded-xl
        ${className}
      `}
    >
      {/* Decorative Border: absolute inset-0 border-8 border-primary/10 rounded-lg pointer-events-none */}
      <div className="absolute inset-0 border-8 border-succession-primary/10 rounded-lg pointer-events-none" />

      {/* Preview Watermark */}
      {isPreview && (
        <div className="absolute top-4 right-4 text-xs font-medium text-succession-text-secondary/50 bg-succession-bg-primary/80 px-2 py-1 rounded">
          PREVIEW
        </div>
      )}

      {/* Inner padding: p-12 (48px) */}
      <div className="relative p-12 space-y-8 text-center">
        {/* Header Icon: w-16 h-16 (64x64px), icon w-8 h-8 (32x32px) */}
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-succession-primary/10 flex items-center justify-center">
            <Award className="w-8 h-8 text-succession-primary" />
          </div>
        </div>

        {/* Title: text-3xl font-bold (30px, 700) */}
        <div className="space-y-2">
          <h2 className="text-3xl font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession.plans.cert_title' })}</h2>
          <p className="text-sm text-succession-text-secondary">
            {intl.formatMessage({ id: 'succession.plans.cert_subtitle' })}
          </p>
        </div>

        {/* Recipient: py-6 border-y border-border (padding: 24px 0) */}
        <div className="space-y-2 py-6 border-y border-succession-border">
          <p className="text-sm text-succession-text-secondary uppercase tracking-wider">
            {intl.formatMessage({ id: 'succession.plans.cert_this_certifies' })}
          </p>
          <p className="text-2xl font-bold text-succession-text-primary">{userName}</p>
        </div>

        {/* Course Details */}
        <div className="space-y-4">
          <p className="text-sm text-succession-text-secondary">
            {intl.formatMessage({ id: 'succession.plans.cert_has_completed' })}
          </p>
          <p className="text-xl font-semibold text-succession-text-primary">{courseName}</p>
          {/* Stats row: flex items-center justify-center gap-8 text-sm */}
          <div className="flex items-center justify-center gap-8 text-sm">
            <div>
              <p className="font-semibold text-succession-text-primary">{creditHours}</p>
              <p className="text-succession-text-secondary">
                {intl.formatMessage({ id: 'succession.plans.cert_credit_hours' }, { hours: creditHours })}
              </p>
            </div>
            {/* Divider: w-px h-8 bg-border */}
            <div className="w-px h-8 bg-succession-border" />
            <div>
              <p className="font-semibold text-succession-text-primary">{completionDate}</p>
              <p className="text-succession-text-secondary">{intl.formatMessage({ id: 'succession.plans.cert_completion_date' })}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="space-y-2 pt-6">
          {sponsorName && (
            <p className="text-sm text-succession-text-secondary">
              {intl.formatMessage({ id: 'succession.plans.cert_sponsored_by' })}{' '}
              <span className="font-medium text-succession-text-primary">{sponsorName}</span>
            </p>
          )}
          <p className="text-xs text-succession-text-secondary">
            {intl.formatMessage({ id: 'succession.plans.cert_certificate_id' }, { id: certificateId })}
          </p>
        </div>
      </div>
    </div>
  );
};

CeCertificatePreview.propTypes = {
  userName: PropTypes.string.isRequired,
  courseName: PropTypes.string.isRequired,
  creditHours: PropTypes.number.isRequired,
  completionDate: PropTypes.string.isRequired,
  sponsorName: PropTypes.string,
  certificateId: PropTypes.string.isRequired,
  isPreview: PropTypes.bool,
  className: PropTypes.string,
};

CeCertificatePreview.defaultProps = {
  sponsorName: null,
  isPreview: false,
  className: '',
};

export default CeCertificatePreview;

